import { Loader } from '@googlemaps/js-api-loader';
import { GoogleApiStatusCodeType } from '/@/modules/core/geo/GoogleApiStatusCodeType';
import Logger from '/@/modules/core/app/helpers/Logger';
import { GoogleAutocompleteSuggestionType } from '/@/modules/core/geo/GoogleAutocompleteSuggestionType';
import { GooglePlacesFieldType } from '/@/modules/core/geo/GooglePlacesFieldType';
import { Env } from '@/Env';

export class GooglePlaceAutoCompleteService {
  /**
   * @type {boolean}
   */
  isApiLoaded;

  /**
   * @type {Loader}
   */
  sdkLoader;

  /**
   * @type {Array.<string>}
   */
  countriesRestrictedTo;

  /**
   * @type {?string}
   */
  sessionToken;

  constructor(countries = ['us', 'ca']) {
    this.countriesRestrictedTo = countries;
  }

  init() {
    if (!Env.nodeEnv().VITE_APP_GOOGLE_API_KEY) {
      Logger.error('Missing Google API key in Environment');
    }

    if (this.sdkLoader) {
      return;
    }

    this.sdkLoader = new Loader({
      apiKey: Env.nodeEnv().VITE_APP_GOOGLE_API_KEY,
      version: 'weekly',
      libraries: ['places']
    });
  }

  /**
   * @param {string} input
   * @param {function} suggestionsCallback
   * @param {array} types
   * @param {boolean} searchGlobal
   */
  async getSuggestions(
    input,
    suggestionsCallback,
    types = [GoogleAutocompleteSuggestionType.SPECIFIC_ADDRESS],
    searchGlobal = false
  ) {
    this.init();

    if (!this.isApiLoaded) {
      await this.sdkLoader.load();
    }
    this.isApiLoaded = true;

    if (!this.sessionToken) {
      this.sessionToken = new google.maps.places.AutocompleteSessionToken();
    }

    const autocompleteService = new google.maps.places.AutocompleteService();

    const componentRestrictions = !searchGlobal
      ? {
          country: this.countriesRestrictedTo
        }
      : null;
    autocompleteService.getPlacePredictions(
      {
        input,
        sessionToken: this.sessionToken,
        componentRestrictions,
        types
      },
      (suggestions, status) => {
        if (status === GoogleApiStatusCodeType.OK) {
          suggestionsCallback(suggestions);
        } else {
          this.processFailedGoogleResponse(status);
          suggestionsCallback([]);
        }
      }
    );
  }

  /**
   * @param {string} placeId
   * @param {function} detailsCallback
   */
  async getPlaceDetails(placeId, detailsCallback) {
    this.init();

    const placesService = new google.maps.places.PlacesService(document.createElement('div'));
    placesService.getDetails(
      {
        placeId,
        sessionToken: this.sessionToken,
        fields: [GooglePlacesFieldType.ADDRESS_COMPONENTS, GooglePlacesFieldType.FORMATTED_ADDRESS]
      },
      (details, status) => {
        this.sessionToken = null;
        if (status === GoogleApiStatusCodeType.OK) {
          detailsCallback(details);
        } else {
          this.processFailedGoogleResponse(status);
          detailsCallback({});
        }
      }
    );
  }

  /**
   * @param {string} status
   */
  processFailedGoogleResponse(status) {
    switch (status) {
      case GoogleApiStatusCodeType.INVALID_REQUEST:
        Logger.error('Google Places API: Check the request payload');
        break;
      case GoogleApiStatusCodeType.OVER_QUERY_LIMIT:
        Logger.error('Google Places API: Query limit has reached');
        break;
      case GoogleApiStatusCodeType.REQUEST_DENIED:
        Logger.error('Google Places API: Request denied by server. Check billing.');
        break;
      case GoogleApiStatusCodeType.ZERO_RESULTS:
        Logger.warning('Google Places API: No results returned');
        break;
      case GoogleApiStatusCodeType.UNKNOWN_ERROR:
      case GoogleApiStatusCodeType.ERROR:
      default:
        Logger.error('Google Places API: General Error');
    }
  }

  /**
   * @returns {string[]}
   */
  getBusinessSuggestionsTypes() {
    return [GoogleAutocompleteSuggestionType.BUSINESS_ADDRESS];
  }
}
